import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { LayoutDashboard, Building2, ClipboardList, LogOut, Shield, ChevronRight, FileStack, ShieldCheck, Link2, Users, Zap, Palette, ClipboardCheck } from "lucide-react";
import { cn } from "@/lib/utils";

const NAV_COMPANY = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/app/aziende", label: "Aziende", icon: Building2, testid: "nav-companies" },
  { to: "/app/audit", label: "Audit", icon: ClipboardList, testid: "nav-audits" },
  { to: "/app/accessi-consulenti", label: "Consulenti", icon: Users, testid: "nav-access" },
  { to: "/app/checklist-azienda", label: "Le mie checklist", icon: ClipboardCheck, testid: "nav-company-checklists" },
];

const NAV_CONSULTANT = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/app/aziende", label: "Aziende", icon: Building2, testid: "nav-companies" },
  { to: "/app/checklist-assegnate", label: "Checklist clienti", icon: ClipboardCheck, testid: "nav-client-checklists" },
  { to: "/app/collegamenti", label: "Aggancia P.IVA", icon: Link2, testid: "nav-link" },
  { to: "/app/audit", label: "Audit", icon: ClipboardList, testid: "nav-audits" },
  { to: "/app/template", label: "Template", icon: FileStack, testid: "nav-templates" },
  { to: "/app/billing", label: "Abbonamento", icon: ShieldCheck, testid: "nav-billing" },
  { to: "/app/impostazioni/ai", label: "Impostazioni AI", icon: Zap, testid: "nav-ai-settings" },
  { to: "/app/impostazioni/branding", label: "Branding", icon: Palette, testid: "nav-branding" },
];

const NAV_ADMIN = [
  { to: "/app/admin", label: "Admin", icon: ShieldCheck, testid: "nav-admin" },
  { to: "/app/team", label: "Team", icon: Users, testid: "nav-team" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  let NAV = NAV_CONSULTANT;
  if (user?.role === "admin") NAV = NAV_ADMIN;
  else if (user?.role === "company_user") NAV = NAV_COMPANY;
  else if (user?.is_tenant_owner) NAV = [...NAV_CONSULTANT, { to: "/app/team", label: "Team", icon: Users, testid: "nav-team" }];

  const roleLabel = user?.role === "admin" ? "Admin" : user?.role === "consultant" ? "Consulente" : user?.role === "company_user" ? "Azienda" : "";
  const roleColor = user?.role === "admin" ? "bg-red-500" : user?.role === "consultant" ? "bg-indigo-500" : "bg-blue-500";

  return (
    <div className="app-shell flex min-h-screen w-full bg-[#F8FAFC]">
      <aside className="hidden md:flex md:flex-col w-64 bg-[#020617] text-slate-100 fixed inset-y-0 left-0 z-30">
        <div className="px-6 py-8 border-b border-slate-800">
          <Link to="/app" className="flex items-center gap-2.5 group" data-testid="brand-link">
            <div className="h-9 w-9 rounded-md bg-blue-600 flex items-center justify-center">
              <Shield className="h-5 w-5 text-white" strokeWidth={2} />
            </div>
            <div>
              <div className="font-display font-black text-lg tracking-tight leading-none">Audit Pro</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mt-1">GDPR · NIS2 · ISO · DORA · AI</div>
            </div>
          </Link>
          {roleLabel && (
            <div className="mt-3 flex items-center gap-2">
              <span className={`inline-block h-1.5 w-1.5 rounded-full ${roleColor}`} />
              <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400" data-testid="role-badge">{roleLabel}</span>
              {user?.tenant_name && <span className="text-[10px] text-slate-500 truncate">· {user.tenant_name}</span>}
            </div>
          )}
        </div>
        <nav className="flex-1 px-3 py-6 space-y-1">
          {NAV.map((n) => {
            const active = n.to === "/" ? location.pathname === "/" : location.pathname.startsWith(n.to);
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-200",
                  active ? "bg-slate-800 text-white" : "text-slate-400 hover:text-white hover:bg-slate-900"
                )}
              >
                <n.icon className="h-4 w-4" strokeWidth={1.5} />
                <span>{n.label}</span>
                {active && <ChevronRight className="ml-auto h-4 w-4 text-blue-400" />}
              </Link>
            );
          })}
        </nav>
        <div className="border-t border-slate-800 px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-slate-800 flex items-center justify-center font-display font-bold text-sm">
              {user?.full_name?.charAt(0)?.toUpperCase() || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" data-testid="user-name">{user?.full_name}</div>
              <div className="text-xs text-slate-500 truncate">{user?.email}</div>
            </div>
            <button
              onClick={() => { logout(); navigate("/auth/login"); }}
              data-testid="logout-btn"
              className="p-2 rounded-md hover:bg-slate-800 text-slate-400 hover:text-red-400 transition-colors duration-200"
              title="Esci"
            >
              <LogOut className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-[#020617] text-white px-4 py-3 flex items-center justify-between">
        <Link to="/app" className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-blue-400" />
          <span className="font-display font-bold">Audit Pro</span>
        </Link>
        <button data-testid="logout-btn-mobile" onClick={() => { logout(); navigate("/auth/login"); }} className="text-slate-400"><LogOut className="h-5 w-5" /></button>
      </div>

      <main className="min-w-0 flex-1 md:ml-64 pt-16 md:pt-0">
        {children}
      </main>
    </div>
  );
}
